/**
 * Widget Version Control Component
 * Enhanced versioning and persistent comments for widget development
 * Created: February 2025
 */

import React, { useState, useEffect } from 'react';
import './FeedbackManager.css';

const WidgetVersionControl = ({ widgetId, currentStatus, onVersionUpdate }) => {
  const [versions, setVersions] = useState([]);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [showAddComment, setShowAddComment] = useState(false);

  // API Base URL
  const getApiBaseUrl = () => {
    const hostname = window.location.hostname;
    if (hostname === 'admin.optiprime.ai') {
      return 'https://admin.optiprime.ai';
    }
    return 'http://5.78.121.92:5002';
  };

  // Fetch widget version data
  const fetchVersionData = async () => {
    setLoading(true);
    try {
      const baseUrl = getApiBaseUrl();
      
      // Fetch versions and comments in parallel
      const [versionsRes, commentsRes] = await Promise.all([
        fetch(`${baseUrl}/api/widget-versioning/${widgetId}/versions`),
        fetch(`${baseUrl}/api/widget-versioning/${widgetId}/comments`)
      ]);

      if (versionsRes.ok && commentsRes.ok) {
        const versionsData = await versionsRes.json();
        const commentsData = await commentsRes.json();
        
        setVersions(versionsData.versions || []);
        setComments(commentsData.comments || []);
      }
    } catch (error) {
      console.error('[VERSION CONTROL] Error fetching data:', error);
    }
    setLoading(false);
  };

  // Create new version (development update)
  const createVersion = async (updateType = 'development', notes = '') => {
    try {
      const baseUrl = getApiBaseUrl();
      const response = await fetch(`${baseUrl}/api/widget-versioning/${widgetId}/versions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          updateType,
          notes,
          createdBy: 'admin'
        })
      });

      if (response.ok) {
        const result = await response.json();
        console.log('[VERSION CONTROL] Version created:', result.version);
        fetchVersionData(); // Refresh data
        onVersionUpdate && onVersionUpdate(result.version);
        return result.version;
      }
    } catch (error) {
      console.error('[VERSION CONTROL] Error creating version:', error);
    }
    return null;
  };

  // Add new comment
  const addComment = async () => {
    if (!newComment.trim()) return;

    try {
      const baseUrl = getApiBaseUrl();
      const latestVersion = versions[0]; // Most recent version
      
      const response = await fetch(`${baseUrl}/api/widget-versioning/${widgetId}/comments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          versionId: latestVersion?.id || null,
          authorType: 'admin',
          authorName: 'Development Team',
          commentText: newComment,
          commentType: 'update',
          isInternal: false
        })
      });

      if (response.ok) {
        const result = await response.json();
        console.log('[VERSION CONTROL] Comment added:', result.comment);
        setNewComment('');
        setShowAddComment(false);
        fetchVersionData(); // Refresh comments
      }
    } catch (error) {
      console.error('[VERSION CONTROL] Error adding comment:', error);
    }
  };

  // Handle development update with version increment
  const handleDevelopmentUpdate = async (notes) => {
    if (!notes.trim()) {
      alert('Please enter update notes before saving.');
      return;
    }

    try {
      console.log('[VERSION CONTROL] Creating development update with notes:', notes);
      const version = await createVersion('development', notes);
      
      if (version) {
        console.log('[VERSION CONTROL] Version created successfully:', version);
        
        // Create a comment for this update
        const baseUrl = getApiBaseUrl();
        const response = await fetch(`${baseUrl}/api/widget-versioning/${widgetId}/comments`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            versionId: version.id,
            authorType: 'admin',
            authorName: 'Development Team',
            commentText: notes,
            commentType: 'update',
            isInternal: false
          })
        });

        if (response.ok) {
          console.log('[VERSION CONTROL] Comment created successfully');
          // Clear the form and refresh data
          setNewComment('');
          setShowAddComment(false);
          fetchVersionData();
        } else {
          console.error('[VERSION CONTROL] Error creating comment');
        }
      }
    } catch (error) {
      console.error('[VERSION CONTROL] Error in handleDevelopmentUpdate:', error);
      alert('Error creating development update. Please try again.');
    }
  };

  // Handle mark as developed (move to v1.00)
  const handleMarkAsDeveloped = async () => {
    const version = await createVersion('testing', 'Widget marked as developed and ready for testing');
    if (version) {
      // Create milestone
      try {
        const baseUrl = getApiBaseUrl();
        await fetch(`${baseUrl}/api/widget-versioning/${widgetId}/milestones`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            milestoneType: 'alpha_release',
            versionNumber: version.version_number,
            description: 'Widget completed development phase and ready for community testing'
          })
        });
      } catch (error) {
        console.error('[VERSION CONTROL] Error creating milestone:', error);
      }
    }
  };

  useEffect(() => {
    if (widgetId) {
      fetchVersionData();
    }
  }, [widgetId]);

  const currentVersion = versions[0]?.version_number || '0.01';
  const totalComments = comments.length;

  // Calculate development velocity metrics
  const calculateVelocityMetrics = () => {
    if (versions.length < 2) return null;
    
    const sortedVersions = [...versions].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    const firstVersion = sortedVersions[0];
    const latestVersion = sortedVersions[sortedVersions.length - 1];
    
    const startTime = new Date(firstVersion.created_at);
    const endTime = new Date(latestVersion.created_at);
    const totalDays = Math.max(1, Math.ceil((endTime - startTime) / (1000 * 60 * 60 * 24)));
    const versionsCount = versions.length - 1; // Exclude initial version
    
    return {
      totalDays,
      versionsCount,
      avgDaysPerVersion: (totalDays / Math.max(1, versionsCount)).toFixed(1),
      startDate: new Date(startTime.toISOString().slice(0, 19) + 'Z').toLocaleDateString('en-US', { timeZone: 'America/Vancouver' }),
      latestDate: new Date(endTime.toISOString().slice(0, 19) + 'Z').toLocaleDateString('en-US', { timeZone: 'America/Vancouver' })
    };
  };

  const velocityMetrics = calculateVelocityMetrics();

  return (
    <div className="widget-version-control">
      {/* Version Status Header */}
      <div className="version-status-header">
        <div className="version-info">
          <span className="version-number">v{currentVersion}</span>
          <span className="version-type">
            {currentVersion.startsWith('0.') ? '🚧 Development' : 
             currentVersion === '1.00' ? '🧪 Testing' : 
             '✅ Production'}
          </span>
        </div>
        <div className="comment-count">
          {totalComments} {totalComments === 1 ? 'update' : 'updates'}
        </div>
        {velocityMetrics && (
          <div className="velocity-metrics">
            <span className="velocity-label">📊 Dev Speed:</span>
            <span className="velocity-value">{velocityMetrics.avgDaysPerVersion} days/version</span>
          </div>
        )}
      </div>

      {/* Quick Actions */}
      <div className="version-actions">
        <button 
          className="action-btn secondary-btn"
          onClick={() => setShowAddComment(!showAddComment)}
        >
          ➕ Add Update (v{getNextDevVersion(currentVersion)})
        </button>
        
        {currentVersion.startsWith('0.') && (
          <button 
            className="action-btn completed-btn"
            onClick={() => {
              if (window.confirm('Mark this widget as developed? This will create v1.00 and move to testing phase.')) {
                handleMarkAsDeveloped();
              }
            }}
          >
            ✅ Mark as Developed (v1.00)
          </button>
        )}
        
        <button 
          className="action-btn secondary-btn"
          onClick={() => setShowVersionHistory(!showVersionHistory)}
        >
          📋 Version History ({versions.length})
        </button>
      </div>

      {/* Add Comment Section */}
      {showAddComment && (
        <div className="add-comment-section">
          <div className="comment-header">
            <strong>📝 Development Update v{getNextDevVersion(currentVersion)}</strong>
          </div>
          <textarea
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            placeholder="Add development progress, technical notes, or status changes..."
            className="comment-textarea"
            rows="4"
          />
          <div className="comment-actions">
            <button 
              className="action-btn primary-btn"
              onClick={() => {
                handleDevelopmentUpdate(newComment);
              }}
            >
              💾 Save Update & Increment Version
            </button>
            <button 
              className="action-btn secondary-btn"
              onClick={() => {
                setNewComment('');
                setShowAddComment(false);
              }}
            >
              ❌ Cancel
            </button>
          </div>
        </div>
      )}

      {/* Version History */}
      {showVersionHistory && (
        <div className="version-history">
          <h4>📋 Version History</h4>
          {velocityMetrics && (
            <div className="velocity-summary">
              <strong>🚀 Development Velocity Analytics:</strong>
              <div className="velocity-stats">
                <span>📅 Period: {velocityMetrics.startDate} → {velocityMetrics.latestDate}</span>
                <span>🔢 Versions: {velocityMetrics.versionsCount} releases in {velocityMetrics.totalDays} days</span>
                <span>⚡ Average: {velocityMetrics.avgDaysPerVersion} days per version</span>
              </div>
            </div>
          )}
          {loading ? (
            <div className="loading">Loading version history...</div>
          ) : versions.length > 0 ? (
            <div className="version-list">
              {versions.map((version, index) => (
                <div key={version.id} className="version-item">
                  <div className="version-header">
                    <span className="version-number">v{version.version_number}</span>
                    <span className={`version-status ${version.status}`}>
                      {getStatusDisplay(version.status)}
                    </span>
                    <span className="version-date">
                      {new Date(version.created_at + 'Z').toLocaleString('en-US', {
                        year: 'numeric',
                        month: 'short', 
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                        second: '2-digit',
                        timeZone: 'America/Vancouver'
                      })}
                    </span>
                  </div>
                  {version.notes && (
                    <div className="version-notes">{version.notes}</div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="no-versions">No version history available</div>
          )}
        </div>
      )}

      {/* Latest Development Update */}
      <div className="comments-history">
        <h4>💬 Development Updates (v{currentVersion})</h4>
        {comments.length > 0 ? (
          <div className="comments-list">
            {/* Show only the latest comment (highest comment_number) */}
            {(() => {
              const latestComment = comments.reduce((latest, current) => 
                current.comment_number > latest.comment_number ? current : latest
              );
              return (
                <div key={latestComment.id} className="comment-item">
                  <div className="comment-header">
                    <span className="comment-number">
                      {latestComment.version_number ? `v${latestComment.version_number}` : `#${latestComment.comment_number}`}
                    </span>
                    <span className="comment-author">{latestComment.author_name}</span>
                    <span className="comment-date">
                      {new Date(latestComment.created_at + 'Z').toLocaleString('en-US', {
                        year: 'numeric',
                        month: 'short', 
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                        second: '2-digit',
                        timeZone: 'America/Vancouver'
                      })}
                    </span>
                  </div>
                  <div className="comment-text">{latestComment.comment_text}</div>
                </div>
              );
            })()}
          </div>
        ) : (
          <div className="no-comments">No development updates yet</div>
        )}
      </div>
    </div>
  );
};

// Helper function to calculate next development version
const getNextDevVersion = (currentVersion) => {
  const [major, minor] = currentVersion.split('.').map(Number);
  if (major === 0) {
    const newMinor = Math.min(minor + 1, 99);
    return `0.${newMinor.toString().padStart(2, '0')}`;
  } else {
    return `${major}.${(minor + 1).toString().padStart(2, '0')}`;
  }
};

// Helper function to display status
const getStatusDisplay = (status) => {
  const statusMap = {
    'pending': '⏳ Pending',
    'in_development': '🚧 In Development', 
    'ready_for_testing': '🧪 Ready for Testing',
    'testing': '🔍 Testing',
    'completed': '✅ Completed',
    'archived': '📦 Archived'
  };
  return statusMap[status] || status;
};

export default WidgetVersionControl;